import { create } from 'zustand';
import { registerService } from '@/services/register.service';
import type { Register, RegisterSession, OpenSessionDto, CloseSessionDto } from '@/services/register.service';
import { useAuthStore } from '@/stores/use-auth-store';

/**
 * Ops simulation T12 — the register this device uses and its open session.
 *
 * The device picks its register once and remembers it (`bbpos_register_id`).
 * A location without registers keeps the old behaviour: bills carry no
 * session and nothing here blocks the till.
 */

const REGISTER_KEY = 'bbpos_register_id';
// `${sessionId}:${userId}` — the signed-in user handed the register to a
// colleague by PIN, so a reload must not take it back.
const HANDOVER_KEY = 'bbpos_register_handover';
// The open session, so a bill rung offline after a reload still carries it.
const SESSION_KEY = 'bbpos_register_session';

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* storage unavailable — the choice lasts until reload */
  }
}

function readSession(): RegisterSession | null {
  const raw = read(SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as RegisterSession;
  } catch {
    return null;
  }
}

function saveSession(session: RegisterSession | null): void {
  write(SESSION_KEY, session && session.status === 'OPEN' ? JSON.stringify(session) : null);
}

export type RegisterReadiness = 'ready' | 'no-registers' | 'needs-register' | 'needs-open';

interface RegisterState {
  /** The location the registers below were loaded for. */
  locationId: string | null;
  registers: Register[];
  registerId: string | null;
  session: RegisterSession | null;
  loading: boolean;

  load: (locationId: string) => Promise<RegisterReadiness>;
  /** null forgets the device's register, to pick another. */
  pickRegister: (registerId: string | null) => Promise<void>;
  openSession: (dto: Omit<OpenSessionDto, 'register_id'>) => Promise<RegisterSession>;
  switchCashier: (userId: string, pin?: string) => Promise<RegisterSession>;
  closeSession: (dto: CloseSessionDto) => Promise<RegisterSession>;
  /** Whether the till may sell at this location; loads when needed. */
  ensureReady: (locationId: string) => Promise<RegisterReadiness>;
}

function readinessOf(s: Pick<RegisterState, 'registers' | 'registerId' | 'session'>): RegisterReadiness {
  if (s.registers.length === 0) return 'no-registers';
  if (!s.registerId || !s.registers.some((r) => r.id === s.registerId)) return 'needs-register';
  if (!s.session || s.session.status !== 'OPEN') return 'needs-open';
  return 'ready';
}

export const useRegisterStore = create<RegisterState>()((set, get) => ({
  locationId: null,
  registers: [],
  registerId: read(REGISTER_KEY),
  session: readSession(),
  loading: false,

  load: async (locationId) => {
    set({ loading: true });
    try {
      const registers = await registerService.listRegisters(locationId);
      const stored = read(REGISTER_KEY);
      const registerId = registers.some((r) => r.id === stored) ? stored : null;
      const register = registers.find((r) => r.id === registerId);
      let session: RegisterSession | null = null;
      if (register?.open_session_id) {
        session = await registerService.getSession(register.open_session_id);
        session = await claimIfNeeded(session);
      }
      saveSession(session);
      set({ locationId, registers, registerId, session });
      return readinessOf({ registers, registerId, session });
    } finally {
      set({ loading: false });
    }
  },

  pickRegister: async (registerId) => {
    write(REGISTER_KEY, registerId);
    saveSession(null);
    set({ registerId, session: null });
    const { locationId } = get();
    if (locationId) await get().load(locationId);
  },

  openSession: async (dto) => {
    const { registerId } = get();
    if (!registerId) throw new Error('Pick a register first');
    const session = await registerService.openSession({ ...dto, register_id: registerId });
    saveSession(session);
    set({ session });
    return session;
  },

  switchCashier: async (userId, pin) => {
    const { session } = get();
    if (!session) throw new Error('The register is not open');
    const updated = await registerService.switchCashier(session.id, userId, pin);
    const me = useAuthStore.getState().user?.sub;
    write(HANDOVER_KEY, me && userId !== me ? `${session.id}:${me}` : null);
    saveSession(updated);
    set({ session: updated });
    return updated;
  },

  closeSession: async (dto) => {
    const { session } = get();
    if (!session) throw new Error('The register is not open');
    const closed = await registerService.closeSession(session.id, dto);
    write(HANDOVER_KEY, null);
    saveSession(null);
    set({ session: null });
    return closed;
  },

  ensureReady: async (locationId) => {
    const state = get();
    if (state.locationId === locationId) {
      const ready = readinessOf(state);
      if (ready === 'ready' || ready === 'no-registers') return ready;
    }
    try {
      return await get().load(locationId);
    } catch {
      // Offline or the API is down: never block the till over registers.
      return state.locationId === locationId ? readinessOf(state) : 'no-registers';
    }
  },
}));

/**
 * After a sign-in the register's cashier is whoever had it before; the
 * signed-in user takes it over — unless they handed it to a colleague by PIN.
 */
async function claimIfNeeded(session: RegisterSession): Promise<RegisterSession> {
  const me = useAuthStore.getState().user?.sub;
  if (!me || session.status !== 'OPEN' || session.current_cashier_id === me) return session;
  if (read(HANDOVER_KEY) === `${session.id}:${me}`) return session;
  try {
    return await registerService.switchCashier(session.id, me);
  } catch {
    return session;
  }
}

/**
 * The API refused a bill because this device's session is closed (closed from
 * another till): forget it, so the next sale sends the cashier to open again.
 */
export function forgetClosedRegisterSession(): void {
  saveSession(null);
  useRegisterStore.setState({ session: null, locationId: null });
}

/** The open session to stamp on a bill at this location, if the device has one. */
export function getActiveRegisterSessionId(locationId: string | null | undefined): string | undefined {
  const { session } = useRegisterStore.getState();
  if (!session || session.status !== 'OPEN') return undefined;
  if (locationId && session.location_id !== locationId) return undefined;
  return session.id;
}
